import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { satelliteMachineService } from "../services/satelliteMachineService";
import {
  SatelliteMachineValuesInit,
  getSatelliteMachineCreateVariables,
  getSatelliteMachineValues,
  SatelliteMachineValuesType,
  SatelliteMachineErrorsType,
  satelliteMachineDisplayId,
} from "./SatelliteMachineCommonStore";
import { SatelliteMachine } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getUserValues,
  UserValuesType,
} from "../../user/store/UserCommonStore";
import { isNil } from "../../../common/utils";
import { GridColumn } from "../../common/types";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class SatelliteMachineCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: SatelliteMachineErrorsType = {};
  service = satelliteMachineService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    identity: { open: true, errors: [] },
    certificate: { open: true, errors: [] },
    authentication: { open: true, errors: [] },
    authorization: { open: true, errors: [] },
    capabilities: { open: true, errors: [] },
    network: { open: true, errors: [] },
    monitoring: { open: true, errors: [] },
    relations: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: SatelliteMachine | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = SatelliteMachineValuesInit();
  nameError: string | undefined;
  machineCodeError: string | undefined;
  satelliteTypeError: string | undefined;
  regionError: string | undefined;
  certificateFingerprintError: string | undefined;
  certificateExpiresAtError: string | undefined;
  authTokenHashError: string | undefined;
  authTokenLastFourError: string | undefined;
  authTokenCreatedAtError: string | undefined;
  authTokenRotatedAtError: string | undefined;
  statusError: string | undefined;
  authorizedAtError: string | undefined;
  authorizedByError: string | undefined;
  authorizedByService = userService;
  authorizedBySearchValue: string = "";
  authorizedByOptions: UserValuesType[] = [];
  suspendedAtError: string | undefined;
  suspendedByError: string | undefined;
  suspendedByService = userService;
  suspendedBySearchValue: string = "";
  suspendedByOptions: UserValuesType[] = [];
  suspensionReasonError: string | undefined;
  enabledError: string | undefined;
  allowedTenantIdsError: string | undefined;
  proxyEnabledError: string | undefined;
  proxyPortError: string | undefined;
  proxyProtocolsError: string | undefined;
  botEnabledError: string | undefined;
  botMaxConcurrentError: string | undefined;
  workerEnabledError: string | undefined;
  publicIpError: string | undefined;
  locationError: string | undefined;
  countryCodeError: string | undefined;
  lastHeartbeatAtError: string | undefined;
  lastHeartbeatMetricsError: string | undefined;
  connectedAtError: string | undefined;
  disconnectedAtError: string | undefined;
  tunnelConnectedAtError: string | undefined;
  tasksError: string | undefined;
  tasksVisibleColumns: GridColumn[];

  private customValidators: CustomValidator<SatelliteMachineValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.tasksVisibleColumns = [
      { name: "taskType", title: this.langStore.SatelliteTaskTaskTypeLabel },
      { name: "status", title: this.langStore.SatelliteTaskStatusLabel },
      { name: "queuedAt", title: this.langStore.SatelliteTaskQueuedAtLabel },
      {
        name: "completedAt",
        title: this.langStore.SatelliteTaskCompletedAtLabel,
      },
      { name: "duration", title: this.langStore.SatelliteTaskDurationLabel },
    ];
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<SatelliteMachineValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<SatelliteMachineValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return satelliteMachineDisplayId(
        this.entityValues as any,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.SatelliteMachineSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity

      if (params.callerData?.fieldName === "authorizedBy") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showAuthorizedBy = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.userOne({ id: params.returnData.resultId });
          const user = result.userOne;
          this.entityValues.showAuthorizedBy = {
            displayId: user.displayId,
            id: user.id,
          };
          this.setAuthorizedByOptions([getUserValues(user, this.langStore)]);
        }
      } else if (params.callerData?.fieldName === "suspendedBy") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showSuspendedBy = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.userOne({ id: params.returnData.resultId });
          const user = result.userOne;
          this.entityValues.showSuspendedBy = {
            displayId: user.displayId,
            id: user.id,
          };
          this.setSuspendedByOptions([getUserValues(user, this.langStore)]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "identity",
        label: this.rootStore.langStore.groupSatelliteMachineIdentityLabel,
        errors: this.getCardErrors("identity"),
      },
      {
        id: "certificate",
        label: this.rootStore.langStore.groupSatelliteMachineCertificateLabel,
        errors: this.getCardErrors("certificate"),
      },
      {
        id: "authentication",
        label:
          this.rootStore.langStore.groupSatelliteMachineAuthenticationLabel,
        errors: this.getCardErrors("authentication"),
      },
      {
        id: "authorization",
        label: this.rootStore.langStore.groupSatelliteMachineAuthorizationLabel,
        errors: this.getCardErrors("authorization"),
      },
      {
        id: "capabilities",
        label: this.rootStore.langStore.groupSatelliteMachineCapabilitiesLabel,
        errors: this.getCardErrors("capabilities"),
      },
      {
        id: "network",
        label: this.rootStore.langStore.groupSatelliteMachineNetworkLabel,
        errors: this.getCardErrors("network"),
      },
      {
        id: "monitoring",
        label: this.rootStore.langStore.groupSatelliteMachineMonitoringLabel,
        errors: this.getCardErrors("monitoring"),
      },
      {
        id: "relations",
        label: this.rootStore.langStore.groupSatelliteMachineRelationsLabel,
        errors: this.getCardErrors("relations"),
      },
    ];
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  getMachineCodeValue(): string {
    return this.entityValues.machineCode;
  }

  setMachineCodeValue(value: string) {
    this.entityValues.machineCode = value;
  }

  getSatelliteTypeValue(): string {
    return this.entityValues.satelliteType;
  }

  setSatelliteTypeValue(value: string) {
    this.entityValues.satelliteType = value;
  }

  getRegionValue(): string {
    return this.entityValues.region;
  }

  setRegionValue(value: string) {
    this.entityValues.region = value;
  }

  getCertificateFingerprintValue(): string {
    return this.entityValues.certificateFingerprint;
  }

  setCertificateFingerprintValue(value: string) {
    this.entityValues.certificateFingerprint = value;
  }

  getCertificateExpiresAtValue(): Date | null {
    return this.entityValues.certificateExpiresAt;
  }

  setCertificateExpiresAtValue(value: Date | null) {
    if (value !== null) {
      this.entityValues.certificateExpiresAt = value;
    }
  }

  getAuthTokenHashValue(): string {
    return this.entityValues.authTokenHash;
  }

  setAuthTokenHashValue(value: string) {
    this.entityValues.authTokenHash = value;
  }

  getAuthTokenLastFourValue(): string {
    return this.entityValues.authTokenLastFour;
  }

  setAuthTokenLastFourValue(value: string) {
    this.entityValues.authTokenLastFour = value;
  }

  getAuthTokenCreatedAtValue(): Date | null {
    return this.entityValues.authTokenCreatedAt;
  }

  setAuthTokenCreatedAtValue(value: Date | null) {
    if (value !== null) {
      this.entityValues.authTokenCreatedAt = value;
    }
  }

  getAuthTokenRotatedAtValue(): Date | null {
    return this.entityValues.authTokenRotatedAt;
  }

  setAuthTokenRotatedAtValue(value: Date | null) {
    this.entityValues.authTokenRotatedAt = value;
  }

  getStatusValue(): string {
    return this.entityValues.status;
  }

  setStatusValue(value: string) {
    this.entityValues.status = value;
  }

  getAuthorizedAtValue(): Date | null {
    return this.entityValues.authorizedAt;
  }

  setAuthorizedAtValue(value: Date | null) {
    this.entityValues.authorizedAt = value;
  }

  setAuthorizedByOptions(values: UserValuesType[]) {
    this.authorizedByOptions = values;
  }

  *loadAuthorizedByValues(): any {
    let where: any = null;
    if (this.authorizedBySearchValue) {
      const searchFilter = {
        OR: [
          {
            email: {
              contains: this.authorizedBySearchValue,
              mode: "insensitive",
            },
          },
          {
            name: {
              contains: this.authorizedBySearchValue,
              mode: "insensitive",
            },
          },
        ],
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.userListWithOffset({
      offset: 0,
      take: 100,
      where: where,
    });
    this.setAuthorizedByOptions(
      (result.userListWithOffset || []).map((x: any) =>
        getUserValues(x, this.langStore)
      )
    );
  }

  getAuthorizedByOptions() {
    return this.authorizedByOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setAuthorizedByValue(value: string) {
    this.authorizedBySearchValue = value;
    yield this.loadAuthorizedByValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedAuthorizedBy(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewUser(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "authorizedBy"
      );
    } else {
      if (value) {
        this.entityValues.showAuthorizedBy = value;
        this.entityValues.__authorizedBy = value.displayId;
      }
    }
  }

  getSuspendedAtValue(): Date | null {
    return this.entityValues.suspendedAt;
  }

  setSuspendedAtValue(value: Date | null) {
    this.entityValues.suspendedAt = value;
  }

  setSuspendedByOptions(values: UserValuesType[]) {
    this.suspendedByOptions = values;
  }

  *loadSuspendedByValues(): any {
    let where: any = null;
    if (this.suspendedBySearchValue) {
      const searchFilter = {
        OR: [
          {
            email: {
              contains: this.suspendedBySearchValue,
              mode: "insensitive",
            },
          },
          {
            name: {
              contains: this.suspendedBySearchValue,
              mode: "insensitive",
            },
          },
        ],
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.userListWithOffset({
      offset: 0,
      take: 100,
      where: where,
    });
    this.setSuspendedByOptions(
      (result.userListWithOffset || []).map((x: any) =>
        getUserValues(x, this.langStore)
      )
    );
  }

  getSuspendedByOptions() {
    return this.suspendedByOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setSuspendedByValue(value: string) {
    this.suspendedBySearchValue = value;
    yield this.loadSuspendedByValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedSuspendedBy(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewUser(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "suspendedBy"
      );
    } else {
      if (value) {
        this.entityValues.showSuspendedBy = value;
        this.entityValues.__suspendedBy = value.displayId;
      }
    }
  }

  getSuspensionReasonValue(): string {
    return this.entityValues.suspensionReason;
  }

  setSuspensionReasonValue(value: string) {
    this.entityValues.suspensionReason = value;
  }

  getEnabledValue(): boolean {
    return this.entityValues.enabled;
  }

  setEnabledValue(value: boolean) {
    this.entityValues.enabled = value;
  }

  getAllowedTenantIdsValue(): string {
    return this.entityValues.allowedTenantIds;
  }

  setAllowedTenantIdsValue(value: string) {
    this.entityValues.allowedTenantIds = value;
  }

  getProxyEnabledValue(): boolean {
    return this.entityValues.proxyEnabled;
  }

  setProxyEnabledValue(value: boolean) {
    this.entityValues.proxyEnabled = value;
  }

  getProxyPortValue(): number | undefined {
    return this.entityValues.proxyPort;
  }

  setProxyPortValue(value: number | undefined) {
    this.entityValues.proxyPort = value;
  }

  getProxyProtocolsValue(): string {
    return this.entityValues.proxyProtocols;
  }

  setProxyProtocolsValue(value: string) {
    this.entityValues.proxyProtocols = value;
  }

  getBotEnabledValue(): boolean {
    return this.entityValues.botEnabled;
  }

  setBotEnabledValue(value: boolean) {
    this.entityValues.botEnabled = value;
  }

  getBotMaxConcurrentValue(): number | undefined {
    return this.entityValues.botMaxConcurrent;
  }

  setBotMaxConcurrentValue(value: number | undefined) {
    this.entityValues.botMaxConcurrent = value;
  }

  getWorkerEnabledValue(): boolean {
    return this.entityValues.workerEnabled;
  }

  setWorkerEnabledValue(value: boolean) {
    this.entityValues.workerEnabled = value;
  }

  getPublicIpValue(): string {
    return this.entityValues.publicIp;
  }

  setPublicIpValue(value: string) {
    this.entityValues.publicIp = value;
  }

  getLocationValue(): string {
    return this.entityValues.location;
  }

  setLocationValue(value: string) {
    this.entityValues.location = value;
  }

  getCountryCodeValue(): string {
    return this.entityValues.countryCode;
  }

  setCountryCodeValue(value: string) {
    this.entityValues.countryCode = value;
  }

  getLastHeartbeatAtValue(): Date | null {
    return this.entityValues.lastHeartbeatAt;
  }

  setLastHeartbeatAtValue(value: Date | null) {
    this.entityValues.lastHeartbeatAt = value;
  }

  getLastHeartbeatMetricsValue(): string {
    return this.entityValues.lastHeartbeatMetrics;
  }

  setLastHeartbeatMetricsValue(value: string) {
    this.entityValues.lastHeartbeatMetrics = value;
  }

  getConnectedAtValue(): Date | null {
    return this.entityValues.connectedAt;
  }

  setConnectedAtValue(value: Date | null) {
    this.entityValues.connectedAt = value;
  }

  getDisconnectedAtValue(): Date | null {
    return this.entityValues.disconnectedAt;
  }

  setDisconnectedAtValue(value: Date | null) {
    this.entityValues.disconnectedAt = value;
  }

  getTunnelConnectedAtValue(): Date | null {
    return this.entityValues.tunnelConnectedAt;
  }

  setTunnelConnectedAtValue(value: Date | null) {
    this.entityValues.tunnelConnectedAt = value;
  }

  addNewUser(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "SatelliteMachine",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/user/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = SatelliteMachineValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      identity: { open: true, errors: [] },
      certificate: { open: true, errors: [] },
      authentication: { open: true, errors: [] },
      authorization: { open: true, errors: [] },
      capabilities: { open: true, errors: [] },
      network: { open: true, errors: [] },
      monitoring: { open: true, errors: [] },
      relations: { open: true, errors: [] },
    };
    this.authorizedBySearchValue = "";
    this.authorizedByOptions = [];
    this.suspendedBySearchValue = "";
    this.suspendedByOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: SatelliteMachineValuesType): SatelliteMachineErrorsType {
    const errors: SatelliteMachineErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineNameLabel,
      };
      this.setCardError("identity", errors.name.message);
    }
    values.machineCode = values.machineCode.trim();
    if (values.machineCode === "") {
      errors.machineCode = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineMachineCodeLabel,
      };
      this.setCardError("identity", errors.machineCode.message);
    }
    values.satelliteType = values.satelliteType.trim();
    if (values.satelliteType === "") {
      errors.satelliteType = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineSatelliteTypeLabel,
      };
      this.setCardError("identity", errors.satelliteType.message);
    }
    values.certificateFingerprint = values.certificateFingerprint.trim();
    if (values.certificateFingerprint === "") {
      errors.certificateFingerprint = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineCertificateFingerprintLabel,
      };
      this.setCardError("certificate", errors.certificateFingerprint.message);
    }
    values.authTokenHash = values.authTokenHash.trim();
    if (values.authTokenHash === "") {
      errors.authTokenHash = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineAuthTokenHashLabel,
      };
      this.setCardError("authentication", errors.authTokenHash.message);
    }
    values.authTokenLastFour = values.authTokenLastFour.trim();
    if (values.authTokenLastFour === "") {
      errors.authTokenLastFour = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineAuthTokenLastFourLabel,
      };
      this.setCardError("authentication", errors.authTokenLastFour.message);
    }
    values.status = values.status.trim();
    if (values.status === "") {
      errors.status = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineStatusLabel,
      };
      this.setCardError("authorization", errors.status.message);
    }

    if (isNil(values.enabled)) {
      errors.enabled = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineEnabledLabel,
      };
      this.setCardError("authorization", errors.enabled.message);
    }

    if (isNil(values.proxyEnabled)) {
      errors.proxyEnabled = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineProxyEnabledLabel,
      };
      this.setCardError("capabilities", errors.proxyEnabled.message);
    }

    if (isNil(values.botEnabled)) {
      errors.botEnabled = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineBotEnabledLabel,
      };
      this.setCardError("capabilities", errors.botEnabled.message);
    }

    if (isNil(values.workerEnabled)) {
      errors.workerEnabled = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.SatelliteMachineWorkerEnabledLabel,
      };
      this.setCardError("capabilities", errors.workerEnabled.message);
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "SatelliteMachineCreateStore"
    );
  }

  *duplicatesSatelliteMachine(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const satelliteMachineInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (satelliteMachineInstance) {
      this.entityValues = getSatelliteMachineValues(
        satelliteMachineInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la edición del registro creado.
      this.rootStore.router.replace(
        `/satelliteMachine/update/${returnData.resultId}`
      );
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewSatelliteMachine(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewSatelliteMachine(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      machineCode: this.entityValues.machineCode,
      satelliteType: this.entityValues.satelliteType,
      region: this.entityValues.region,
      certificateFingerprint: this.entityValues.certificateFingerprint,
      certificateExpiresAt: this.entityValues.certificateExpiresAt,
      authTokenHash: this.entityValues.authTokenHash,
      authTokenLastFour: this.entityValues.authTokenLastFour,
      authTokenCreatedAt: this.entityValues.authTokenCreatedAt,
      authTokenRotatedAt: this.entityValues.authTokenRotatedAt,
      status: this.entityValues.status,
      authorizedAt: this.entityValues.authorizedAt,
      suspendedAt: this.entityValues.suspendedAt,
      suspensionReason: this.entityValues.suspensionReason,
      enabled: this.entityValues.enabled,
      allowedTenantIds: this.entityValues.allowedTenantIds,
      proxyEnabled: this.entityValues.proxyEnabled,
      proxyPort: this.entityValues.proxyPort,
      proxyProtocols: this.entityValues.proxyProtocols,
      botEnabled: this.entityValues.botEnabled,
      botMaxConcurrent: this.entityValues.botMaxConcurrent,
      workerEnabled: this.entityValues.workerEnabled,
      publicIp: this.entityValues.publicIp,
      location: this.entityValues.location,
      countryCode: this.entityValues.countryCode,
      lastHeartbeatAt: this.entityValues.lastHeartbeatAt,
      lastHeartbeatMetrics: this.entityValues.lastHeartbeatMetrics,
      connectedAt: this.entityValues.connectedAt,
      disconnectedAt: this.entityValues.disconnectedAt,
      tunnelConnectedAt: this.entityValues.tunnelConnectedAt,
      authorizedBy: this.entityValues.showAuthorizedBy?.id || null,
      suspendedBy: this.entityValues.showSuspendedBy?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getSatelliteMachineValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = SatelliteMachineValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const variables = getSatelliteMachineCreateVariables(this.entityValues);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.satelliteMachineCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.satelliteMachineCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.satelliteMachineCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesSatelliteMachine(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.name = initialDisplayId;
      }

      yield this.loadAuthorizedByValues();
      yield this.loadSuspendedByValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
